import type { ContainerProps } from "@chakra-ui/react"
import { Container, Stack } from "@chakra-ui/react"
import type { ReactNode } from "react"
import { useId } from "react"

import { PageHeader, type PageHeaderProps } from "./PageHeader"

const DEFAULT_MAX_WIDTH = "1280px"

type ContainerSpacingProps = Pick<
  ContainerProps,
  "maxW" | "minH" | "px" | "py"
> & {
  contentGap?: number | string
}

type ContainerRestProps = Omit<
  ContainerProps,
  keyof ContainerSpacingProps | "children"
>

export interface PageShellProps
  extends Omit<PageHeaderProps, "headingId">,
    ContainerSpacingProps,
    ContainerRestProps {
  children: ReactNode
  disableHeader?: boolean
  fullWidth?: boolean
}

export function PageShell({
  title,
  description,
  breadcrumbs,
  actions,
  children,
  disableHeader = false,
  fullWidth = false,
  maxW,
  minH,
  px = { base: 4, md: 8 },
  py = 6,
  contentGap = 6,
  ...rest
}: PageShellProps) {
  const headingId = useId()
  const fallbackLabel = breadcrumbs?.at(-1)?.label
  const ariaLabel = title ? undefined : fallbackLabel

  const header = (
    <PageHeader
      title={title}
      description={description}
      actions={actions}
      breadcrumbs={breadcrumbs}
      headingId={title ? headingId : undefined}
    />
  )

  return (
    <Container
      as="section"
      aria-label={ariaLabel}
      aria-labelledby={title ? headingId : undefined}
      maxW={fullWidth ? "100%" : maxW ?? DEFAULT_MAX_WIDTH}
      minH={minH}
      px={px}
      py={py}
      {...rest}
    >
      <Stack gap={contentGap}>
        {!disableHeader && header}
        {children}
      </Stack>
    </Container>
  )
}
